/**
 * backNavigation — central step-by-step back-navigation manager.
 *
 * Goal: one back press = one step back, everywhere (touch hardware back,
 * browser back, swipe-back gesture, in-app back buttons, Escape).
 *
 * Priority order for a back press:
 *   1. Close the topmost overlay (modal / drawer / panel / FAB menu).
 *   2. Otherwise go one page back in history (with safe fallback).
 *
 * Implementation: a single shared history guard entry. The first open
 * overlay pushes ONE `pushState` entry; additional stacked overlays only
 * extend the in-memory stack. Each hardware/browser back therefore pops
 * exactly one layer: the handler closes only the top overlay and, if more
 * overlays remain, re-arms the guard. Manual closes (X button, backdrop
 * tap, in-app back) unwind the guard via `history.back()` so history never
 * gets polluted with stale entries.
 */

const GUARD_KEY = '__miventOverlay';

let stack = []; // [{ id, label, close }]
let listeners = new Set();
let guardActive = false;
let suppressNextPop = false;
let idCounter = 0;

function notify() {
  listeners.forEach((fn) => {
    try {
      fn(stack.length);
    } catch {
      /* ignore listener errors */
    }
  });
}

function hasGuardState() {
  try {
    return !!(
      typeof window !== 'undefined' &&
      window.history &&
      window.history.state &&
      window.history.state[GUARD_KEY]
    );
  } catch {
    return false;
  }
}

function pushGuard() {
  try {
    window.history.pushState({ [GUARD_KEY]: true }, '');
    guardActive = true;
  } catch {
    guardActive = false;
  }
}

/** History back that will be ignored by our own popstate handler. */
function silentBack() {
  suppressNextPop = true;
  try {
    window.history.back();
  } catch {
    suppressNextPop = false;
  }
  guardActive = false;
}

/**
 * Register an open overlay. Returns an id for unregistering.
 * @param {Function} close - idempotent close callback (e.g. () => setOpen(false))
 * @param {string} label - debug label
 */
export function registerOverlay(close, label = 'overlay') {
  if (typeof window === 'undefined' || typeof close !== 'function') return -1;
  const id = ++idCounter;
  const wasEmpty = stack.length === 0;
  stack.push({ id, label, close });
  if (wasEmpty && !guardActive) pushGuard();
  notify();
  return id;
}

/**
 * Unregister an overlay (called from component unmount / close cleanup).
 * If this was the last overlay and our guard entry is still on top of the
 * history stack (i.e. a manual close, not a back-press), unwind it silently.
 */
export function unregisterOverlay(id) {
  if (typeof window === 'undefined' || id == null) return;
  const idx = stack.findIndex((entry) => entry.id === id);
  if (idx === -1) return; // already consumed by a history pop — nothing to do
  stack.splice(idx, 1);
  if (stack.length === 0 && guardActive) {
    if (hasGuardState()) {
      silentBack();
    } else {
      guardActive = false;
    }
  }
  notify();
}

/**
 * Programmatic "back one overlay" (in-app back buttons, Escape, native
 * back-button interception). Calls the top overlay's close and lets its
 * effect cleanup unwind the stack/history. Returns true if handled.
 */
export function closeTopOverlay() {
  if (stack.length === 0) return false;
  const top = stack[stack.length - 1];
  try {
    top.close();
  } catch {
    // Fallback: drop it synchronously so back never gets stuck.
    stack.pop();
    if (stack.length === 0 && guardActive) {
      if (hasGuardState()) silentBack();
      else guardActive = false;
    }
    notify();
  }
  return true;
}

export function hasOverlay() {
  return stack.length > 0;
}

export function overlayDepth() {
  return stack.length;
}

export function onOverlayChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Single global popstate handler (see SmartBackHandler).
 * @returns {'suppressed'|'overlay'|'none'}
 */
export function handleOverlayHistoryPop() {
  if (suppressNextPop) {
    suppressNextPop = false;
    guardActive = stack.length > 0;
    return 'suppressed';
  }
  if (stack.length === 0) {
    guardActive = false;
    return 'none'; // no overlay open — let the router do a normal page back
  }
  // User pressed back with overlay(s) open: the browser already popped our
  // guard entry. Close ONLY the top overlay (one press = one step).
  const top = stack.pop();
  guardActive = false;
  try {
    top.close();
  } catch {
    /* close callbacks must be idempotent; ignore errors */
  }
  // The component's cleanup will call unregisterOverlay(id), which is now a
  // no-op since we already popped it. If overlays remain, re-arm the guard
  // so the NEXT back press closes the next layer instead of leaving the page.
  if (stack.length > 0) pushGuard();
  notify();
  return 'overlay';
}

/**
 * Smart page-back with safe fallback for direct launches (no history).
 * Call ONLY when no overlay was open (overlays take precedence).
 * @returns {boolean} true if navigation was performed
 */
export function goPageBack(navigate, fallback = '/dashboard') {
  try {
    if (typeof window !== 'undefined' && window.history && window.history.length > 1) {
      navigate(-1);
      return true;
    }
  } catch {
    /* fall through to fallback */
  }
  try {
    navigate(fallback, { replace: true });
  } catch {
    try {
      window.location.assign(fallback);
    } catch {
      /* last resort: stay put */
    }
  }
  return true;
}
